import React, {useContext, useEffect, useState} from "react";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import {env} from "../../properties";
import {styled} from "@mui/system";
import {Box, ClickAwayListener, Collapse, IconButton} from "@mui/material";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import {NavLink} from "react-router-dom";
import {isEmpty} from "lodash-es";
import GenericMenuItem from "./GenericMenuItem";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import {toolbarHeight} from "../../utils/commonValues";
import MobileMenu from "./MobileMenu";
import Rendered from "../Rendered";
import NavigationToolbar from "./NavigationToolbar";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ProfileButton from "./ProfileButton";
import ContactInfo from "./ContactInfo";
import {CompanyEnvironmentContext} from "../../context/CompanyEnvironmentContext";
import {hasMscRight, hasTpRight} from "../../utils/commonFunctions";
import {CustomerContext} from "../../context/CustomerContext";

const NavStyled = styled('nav')(({theme}) => ({
  [theme.breakpoints.down('lg')]: {
    flexGrow: 1,
    paddingLeft: 15
  },
  [theme.breakpoints.up("lg")]: {
    display: "flex",
    justifyContent: "space-evenly",
    flexGrow: 1,
    paddingTop: 1,
    paddingBottom: 1
  }
}));

const ImageStyled = styled("img")(({theme}) => ({
  [theme.breakpoints.up("lg")]: {
    height: 60
  },
  height: 35
}));

const BoxStyled = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
}));

const ButtonStyled = styled(Button, {
  shouldForwardProp: (prop) => !["active"].includes(prop)
})(({theme, ...props}) => ({
  textTransform: "none",
  fontSize: "13px",
  fontWeight: "bold",
  color: props.active ? theme.palette.userBar.background : theme.palette.primary.bgColor,
  backgroundColor: props.active ? theme.palette.primary.bgColor : theme.palette.userBar.background,
  "&:hover": {
    color: theme.palette.userBar.background,
    backgroundColor: theme.palette.primary.bgColor
  }
}));

const TopMenu = ({withIcon, renderMiniMenu, drawerOpen, setDrawerOpen}) => {
  const customerContext = useContext(CustomerContext);
  const companyEnvironmentContext = useContext(CompanyEnvironmentContext);
  const [selectedMenuItem, setSelectedMenuItem] = useState({});
  const [collapseOpen, setCollapseOpen] = useState(false);

  useEffect(() => {
    if (!isEmpty(selectedMenuItem)) {
      setCollapseOpen(true);
    }
  }, [selectedMenuItem]);

  const handleMenuOpen = (event, menuItem) => {
    event.preventDefault();
    setSelectedMenuItem(curr => {
      if (curr.label === menuItem.label) {
        setCollapseOpen(false);
      }
      return menuItem;
    });
  };

  const handleClickAway = () => setCollapseOpen(false);

  return (
    <Box>
      <AppBar
        position="sticky"
        color="inherit"
        elevation={1}
        sx={{zIndex: theme => theme.zIndex.drawer + 1}}
      >
        <Rendered rendered={!renderMiniMenu}>
          <Box sx={{backgroundColor: "userBar.background", paddingLeft: 1, paddingRight: 1}}>
            <Box
              className="container-lg"
              sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
            >
              <ButtonGroup
                variant="contained"
                size="small"
                sx={{
                  margin: 0,
                  padding: 0,
                  "& .MuiButton-root": {
                    borderRadius: "10px 10px 0 0"
                  }
                }}
              >
                <ButtonStyled active>{"Business Portal"}</ButtonStyled>
                <Rendered rendered={!!env?.tpRoot && !!hasTpRight(customerContext.selectedContact.roles)}>
                  <ButtonStyled onClick={() => window.open(env?.tpRoot, "_blank")}>{"Technical Portal"}</ButtonStyled>
                </Rendered>
                <Rendered rendered={!!env?.mscRoot && !!hasMscRight(customerContext.selectedContact.roles)}>
                  <ButtonStyled onClick={() => window.open(env?.mscRoot, "_blank")}>{"Mobile Selfcare"}</ButtonStyled>
                </Rendered>
              </ButtonGroup>
              <ContactInfo appBar/>
            </Box>
          </Box>
        </Rendered>
        <Toolbar
          variant="dense"
          disableGutters
          sx={{backgroundColor: renderMiniMenu ? "secondary.main" : "primary.contrastText"}}
        >
          <NavStyled className="container-lg">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: renderMiniMenu ? "flex-start" : "space-between",
                width: "100%"
              }}
            >
              <BoxStyled sx={{height: toolbarHeight(renderMiniMenu)}}>
                <BoxStyled component={NavLink} to={{pathname: "/", state: {from: window.location.pathname}}}>
                  <ImageStyled
                    src={renderMiniMenu ? companyEnvironmentContext.logos.fullWhite : companyEnvironmentContext.logos.fullLightBg}
                    alt={companyEnvironmentContext.companyEnvironment}
                  />
                </BoxStyled>
                <Rendered rendered={!renderMiniMenu}>
                  <Box sx={{display: "flex", alignItems: "center", justifyContent: "center"}}>
                    <Divider orientation="vertical" variant="middle" flexItem sx={{paddingLeft: 1.5}}/>
                    <ClickAwayListener onClickAway={handleClickAway}>
                      <List dense>
                        {env.menuItems.map(menuItem =>
                          <GenericMenuItem
                            key={menuItem.key || menuItem.url}
                            menuItem={menuItem}
                            onClick={(event, menuItem) => menuItem.children ? handleMenuOpen(event, menuItem) : handleClickAway}
                            parent
                            selectedMenuItem={selectedMenuItem}
                            withIcon={withIcon}
                          />
                        )}
                      </List>
                    </ClickAwayListener>
                  </Box>
                </Rendered>
              </BoxStyled>
              <Rendered rendered={!renderMiniMenu}>
                <Box sx={{display: "flex", placeContent: "flex-end", alignItems: "center", justifyContent: "center"}}>
                  <NavigationToolbar renderMiniMenu={renderMiniMenu}/>
                </Box>
              </Rendered>
            </Box>
          </NavStyled>
          <Rendered rendered={!!renderMiniMenu}>
            <Box sx={{display: "flex", right: 0}}>
              <ProfileButton renderMiniMenu/>
              <IconButton
                sx={{color: "primary.contrastText"}}
                size="large"
                edge="start"
                onClick={() => setDrawerOpen(!drawerOpen)}
              >
                {drawerOpen ? <CloseIcon/> : <MenuIcon/>}
              </IconButton>
            </Box>
          </Rendered>
        </Toolbar>
      </AppBar>
      <Rendered rendered={!renderMiniMenu}>
        <Collapse
          sx={{
            width: "100%",
            position: "absolute",
            zIndex: theme => theme.zIndex.appBar + 1
          }}
          in={collapseOpen}
          onExited={() => setSelectedMenuItem(curr => collapseOpen ? curr : {})}
          timeout="auto"
          unmountOnExit
          orientation="vertical"
        >
          <Paper
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              paddingTop: 1,
              paddingBottom: 2
            }}
            square
            elevation={1}
          >
            <Box sx={{width: "70%"}}>
              <Box sx={{display: "flex", justifyContent: "flex-start"}}>
                <List
                  dense
                  sx={{
                    display: "flex",
                    flexFlow: "column wrap",
                    maxHeight: 150,
                    columnGap: 15
                  }}
                >
                  {selectedMenuItem.children?.map(subMenuItem => (
                    <GenericMenuItem
                      key={subMenuItem.url + "_submenu"}
                      menuItem={subMenuItem}
                      onClick={handleClickAway}
                      selectedMenuItem={selectedMenuItem}
                      withIcon={withIcon}
                    />
                  ))}
                </List>
              </Box>
            </Box>
          </Paper>
        </Collapse>
      </Rendered>
      <Rendered rendered={!!renderMiniMenu}>
        <MobileMenu open={drawerOpen} setDrawerOpen={setDrawerOpen}/>
      </Rendered>
    </Box>
  );
};

TopMenu.propTypes = {
  withIcon: PropTypes.bool,
  renderMiniMenu: PropTypes.bool,
  drawerOpen: PropTypes.bool,
  setDrawerOpen: PropTypes.func
};

export default TopMenu;
